import Link from 'next/link'
import { IFanQna } from '@/api/interfaces/IFanQna'
import { Avatar } from '../Avatar'
import { Card } from '../Card'
import { fromNow } from '@/util/datetime'
import { WithT } from '@/locales/types'
import { removeAllTags } from '../editor/html'
import { IncrementalButton } from '../buttons/IncrementalButton'
import { Icon } from '../Icon'
import { formatNumber } from '@/util/number'
import { MoreButton } from '../buttons/MoreButton'
import { AuthUser, useAuthStore } from '@/store/auth'
import { createT } from '@/locales/index'
import { useDialogStore } from '../dialog/FullScreenBackDrop'
import { useRouter } from 'next/navigation'
import { FanQnaApi } from '@/api/fanqna'
import { ImageWithFallback } from '../ImageWithFallback'

interface FanAnswerCardProps {
  item: IFanQna.IAnswerRO
  questionId: number
  user?: AuthUser
}

export const FanAnswerCard = ({
  item,
  user,
  questionId,
  translations,
}: WithT<FanAnswerCardProps>) => {
  const t = createT(translations)
  const dialog = useDialogStore()
  const router = useRouter()
  const { accessToken } = useAuthStore()

  const reportMenu = [
    {
      id: 'REPORT',
      text: t('report'),
    },
  ]

  const editMenu = [
    {
      id: 'EDIT',
      text: t('edit'),
    },
    {
      id: 'DELETE',
      text: t('delete'),
    },
  ]

  const handleMenuClick = async (id: string) => {
    if (id === 'EDIT') {
      router.push(
        `/questions/edit?username=${item.creator.username}&metaId=${questionId}&answerId=${item.id}`,
      )
    }

    if (id === 'DELETE') {
      dialog.alert({
        title: t('dialog-delete-confirm-title'),
        cancelable: true,
        onConfirm: async () => {
          await FanQnaApi(accessToken).delete(item.id)
          router.refresh()
          dialog.pop()
          router.replace(`/@${item.creator.username}/questions/${questionId}`)
        },
      })
    }

    if (id === 'REPORT') {
      dialog.push('report')
    }
  }

  return (
    <Link
      href={`/@${item.creator.username}/questions/${questionId}/answers/${item.id}`}
    >
      <Card className="bg-gray-50 flex flex-col gap-2">
        <div className="flex items-center gap-2">
          <Avatar size={30} image={item.writer} />
          <div className="flex-1 flex flex-col">
            <div className="flex items-center gap-1">
              <p className="text-body2 font-medium text-gray-900">
                {item.writer.nickname}
              </p>
            </div>

            <p className="text-caption1 text-gray-600" suppressHydrationWarning>
              {fromNow(item.createDate, translations)}
            </p>
          </div>

          <div data-action-button>
            <MoreButton
              menu={
                item.writer.username === user?.username ? editMenu : reportMenu
              }
              onClick={handleMenuClick}
            />
          </div>
        </div>
        <div className="flex gap-2">
          {/* FIXME: item 추가 필요 */}
          {!!item.thumbnailUrl && (
            <div className="w-[72px] h-[72px] rounded-lg overflow-hidden relative shrink-0">
              <ImageWithFallback
                alt="팬 Q&A 썸네일"
                src={item.thumbnailUrl}
                width={200}
                height={200}
                className="h-full object-cover"
              />
            </div>
          )}
          <div className="flex flex-col gap-1.5">
            <p className="break-all text-body1 leading-tight line-clamp-4 text-gray-900 flex-1 whitespace-pre-line">
              {removeAllTags(item.body)
                .trim()
                .replace(/\n{2,}/g, '\n')}
            </p>
          </div>
        </div>

        <div className="flex gap-4">
          {/* <IncrementalButton
            initialCount={item.likeCount}
            initialActive={item.isLike}
            className="flex items-center gap-1"
            // onClick={onClickLike}
          >
            {(v, active) => (
              <>
                <Icon
                  name={active ? 'heart-fill' : 'heart-line'}
                  size={20}
                  className={active ? '!text-key' : '!text-gray-500'}
                />
                <span className="text-gray-500 text-body1">
                  {formatNumber(v)}
                </span>
              </>
            )}
          </IncrementalButton> */}
          <div className="flex items-center gap-1">
            <Icon
              name={item.isLike ? 'heart-fill' : 'heart-line'}
              size={20}
              className={item.isLike ? '!text-key' : '!text-gray-500'}
            />
            <span className="text-gray-500 text-body1">
              {formatNumber(item.likeCount)}
            </span>
          </div>

          <div className="flex items-center gap-1">
            <Icon name="message-line" size={20} className="!text-gray-500" />
            <span className="text-gray-500 text-body1">
              {formatNumber(item.commentCount)}
            </span>
          </div>
        </div>
      </Card>
    </Link>
  )
}
